if (!customElements.get('discover-favorites-rail')) {
  customElements.define('discover-favorites-rail', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.slider = this.querySelector('slider-element');
      this.products = this.querySelector('.discover-favorites__products');
      if (!this.slider || !this.products) return;
      this.cards = Array.from(this.products.querySelectorAll('.card')).filter((card) => card.parentElement === this.products || card.parentElement.classList.contains('discover-favorites__page'));
      const root = this.closest('discover-favorites');
      this.querySelectorAll('quick-view').forEach((modal) => root.append(modal));
      Array.from(this.products.children).filter((node) => node.tagName === 'SCRIPT').forEach((script) => this.append(script));
      this.mobile = window.matchMedia('(max-width: 767px)');
      this.scrollbar = this.querySelector('.discover-favorites__scrollbar-input');
      const options = { signal: this.controller.signal };
      this.mobile.addEventListener('change', () => this.build(), options);
      this.slider.addEventListener('scroll', () => this.updateScrollbar(), { ...options, passive: true });
      this.scrollbar?.addEventListener('input', () => {
        const pages = this.products.querySelectorAll('.discover-favorites__page');
        const page = Array.from(pages).find((page) => Number(page.dataset.pageIndex) === Number(this.scrollbar.value));
        if (page) this.slider.scrollToPosition(page.offsetLeft - pages[0].offsetLeft, true);
      }, options);
      ['slider:previous', 'slider:next'].forEach((type) => {
        this.slider.addEventListener(type, (event) => {
          if (event.target !== this.slider || !this.hasAttribute('data-carousel')) return;
          event.stopImmediatePropagation();
          let pages = this.slider.itemsToShow;
          if (pages.length < 2) return;
          const offset = Math.abs(pages[1].offsetLeft - pages[0].offsetLeft);
          if (!offset) return;
          const current = Math.round(Math.abs(this.slider.scrollLeft) / offset);
          const direction = type === 'slider:next' ? 1 : -1;
          let next = current + direction;
          if (next < 0 || next >= pages.length) {
            const active = pages[current];
            if (direction > 0) this.products.append(pages[0]);
            else this.products.prepend(pages.at(-1));
            this.slider.reset();
            pages = this.slider.itemsToShow;
            const position = active.offsetLeft - pages[0].offsetLeft;
            this.slider.scrollToPosition(position, true);
            next = pages.indexOf(active) + direction;
          }
          this.slider.scrollToPosition(pages[next].offsetLeft - pages[0].offsetLeft);
        }, { ...options, capture: true });
      });
      ['previous', 'next'].forEach((direction) => {
        this.addEventListener(`slider:${direction}Status`, (event) => {
          if (event.target !== this.slider) return;
          if (this.hasAttribute('data-carousel') && this.slider.itemsToShow.length > 1) event.detail.status = 'visible';
          const button = this.querySelector(`.discover-favorites__arrow--${direction}`);
          if (!button) return;
          button.hidden = event.detail.status === 'hidden' || (direction === 'previous' && event.detail.status === 'disabled');
          button.disabled = event.detail.status === 'disabled';
        }, { ...options, capture: true });
      });
      this.slider.addEventListener('keydown', (event) => {
        if (event.target !== this.slider || !this.hasAttribute('data-carousel')) return;
        const rtl = getComputedStyle(this.slider).direction === 'rtl';
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.slider.dispatchEvent(new CustomEvent((event.key === 'ArrowRight') !== rtl ? 'slider:next' : 'slider:previous'));
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          const pages = this.slider.itemsToShow;
          const index = event.key === 'Home' ? 0 : pages.length - 1;
          const page = pages.find((page) => Number(page.dataset.pageIndex) === index);
          if (page) this.slider.scrollToPosition(page.offsetLeft - pages[0].offsetLeft);
        }
      }, options);
      this.build();
      this.resize?.disconnect();
      this.resize = new ResizeObserver(() => this.refresh());
      this.resize.observe(this.slider);
      Promise.all(['slider-element', 'previous-button', 'next-button'].map((name) => customElements.whenDefined(name))).then(() => {
        if (this.isConnected) this.refresh();
      });
    }

    build() {
      const firstCard = (this.pageSize || 4) * this.currentPageIndex();
      const carousel = this.cards.length > 4;
      this.toggleAttribute('data-carousel', carousel);
      this.pageSize = 4;
      if (carousel) {
        const pages = [];
        for (let index = 0; index < this.cards.length; index += this.pageSize) {
          const page = document.createElement('div');
          page.className = 'discover-favorites__page';
          page.dataset.pageIndex = String(pages.length);
          page.append(...this.cards.slice(index, index + this.pageSize));
          pages.push(page);
        }
        this.products.replaceChildren(...pages);
      } else {
        this.products.replaceChildren(...this.cards);
      }
      this.setAttribute('data-ready', '');
      this.slider.reset?.();
      const direction = getComputedStyle(this.slider).direction === 'rtl' ? -1 : 1;
      this.slider.scrollLeft = carousel ? direction * Math.floor(firstCard / this.pageSize) * this.slider.clientWidth : 0;
      this.formatPrices();
      this.refresh();
    }

    refresh() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected || !this.slider.clientWidth) return;
        this.slider.reset?.();
        if (this.hasAttribute('data-carousel') && this.slider.update) this.slider.update();
        this.slider.updateTabindex?.();
        this.updateScrollbar();
      });
    }

    currentPageIndex() {
      const pages = Array.from(this.products.querySelectorAll('.discover-favorites__page'));
      const offset = pages.length > 1 ? Math.abs(pages[1].offsetLeft - pages[0].offsetLeft) : 0;
      const current = offset ? Math.min(pages.length - 1, Math.round(Math.abs(this.slider.scrollLeft) / offset)) : 0;
      return Number(pages[current]?.dataset.pageIndex) || 0;
    }

    updateScrollbar() {
      if (!this.scrollbar) return;
      const pageCount = Math.ceil(this.cards.length / 4);
      this.scrollbar.max = String(Math.max(0, pageCount - 1));
      this.scrollbar.style.setProperty('--discover-scrollbar-thumb-width', `${100 / Math.max(1, pageCount)}%`);
      const current = this.currentPageIndex();
      this.scrollbar.value = String(current);
      this.scrollbar.setAttribute('aria-valuetext', `${current + 1} / ${Math.max(1, pageCount)}`);
    }

    formatPrices() {
      this.products.querySelectorAll('.price__regular').forEach((price) => {
        if (price.closest('.common-product-card__price--split')) return;
        if (price.querySelector('.discover-favorites__price-integer')) return;
        const match = price.textContent.trim().match(/^(\D*?)(\d[\d.,\s]*?)([.,]\d{2})?(\D*)$/);
        if (!match) return;
        const classes = ['prefix', 'integer', 'fraction', 'suffix'];
        price.replaceChildren(...match.slice(1).map((value, index) => {
          const span = document.createElement('span');
          span.className = `discover-favorites__price-${classes[index]}`;
          span.textContent = value || '';
          return span;
        }));
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  });
}

if (!customElements.get('discover-favorites')) {
  customElements.define('discover-favorites', class extends HTMLElement {
    connectedCallback() {
      this.navigation = this.querySelector('tabs-element');
      if (!this.navigation) return;
      this.controller?.abort();
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.buttons = Array.from(this.navigation.querySelectorAll('[role="tab"]'));
      this.navigation.addEventListener('keydown', (event) => {
        const index = this.buttons.indexOf(event.target);
        if (index < 0) return;
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        let next = index;
        if (event.key === 'ArrowRight') next += direction;
        else if (event.key === 'ArrowLeft') next -= direction;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.buttons.length - 1;
        else return;
        event.preventDefault();
        this.navigation.selectedIndex = (next + this.buttons.length) % this.buttons.length;
        this.sync();
        this.buttons[this.navigation.selectedIndex].focus();
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const index = this.buttons.findIndex((button) => button === event.target || button.contains(event.target));
        if (index >= 0) this.navigation.selectedIndex = index;
      }, options);
      this.observer?.disconnect();
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.navigation, { attributes: true, attributeFilter: ['selected-index'] });
      this.sync();
    }

    sync() {
      const selected = Number(this.navigation.getAttribute('selected-index')) || 0;
      this.querySelectorAll('[data-discover-banner]').forEach((banner, index) => {
        banner.hidden = index !== selected;
      });
      this.buttons.forEach((button, index) => {
        button.disabled = false;
        button.setAttribute('aria-selected', String(index === selected));
        button.tabIndex = index === selected ? 0 : -1;
      });
      const button = this.buttons[selected];
      if (button) {
        const panel = this.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        panel?.querySelector('discover-favorites-rail')?.refresh();
        const tab = button.getBoundingClientRect();
        const navigation = this.navigation.getBoundingClientRect();
        if (tab.left < navigation.left || tab.right > navigation.right) {
          this.navigation.scrollLeft += tab.left - navigation.left - (navigation.width - tab.width) / 2;
        }
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }
  });
}
